// ============================================================
// INFRA DIGEST - SITE CONTENT
// Pass two: content corrections applied. No em or en dashes.
// ============================================================

import { Mail, MapPin } from "lucide-react";

export const SITE = {
  name: "Infra Digest",
  tagline: "India Infrastructure Knowledge Hub",
  playStoreUrl: "https://play.google.com/store/apps/details?id=com.ashish.infradigest",
  footerNote:
    "Infra Digest is an independent educational platform. It is not a government website, and it is not affiliated with any government body or institution.",
};

export const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Sectors", href: "#sectors" },
  { label: "Latest update", href: "#latest-update" },
  { label: "Download", href: "#download" },
  { label: "Contact", href: "#contact" },
];

export const HERO = {
  eyebrow: "India Infrastructure Knowledge Hub",
  titleLines: [
    { text: "India's Infrastructure." },
    { text: "All in One Place.", className: "font-normal text-accent" },
  ],
  paragraphs: [
    "Infrastructure is a cornerstone of every thriving economy, supporting connectivity, driving growth and enhancing the quality of life for citizens.",
    "Infra Digest is a compact offline knowledge app that makes it easier to explore and understand India's infrastructure sector through a structured collection of technical concepts, policy frameworks, regulations, institutional mechanisms and sector insights.",
  ],
  closing:
    "One platform. Multiple infrastructure sectors. Reliable knowledge at your fingertips.",
  primaryCta: "Get it on Google Play",
  secondaryCta: "Explore the Sectors",
  image: "/images/infra_improved.webp",
  image2x: "/images/infra_improved-2x.webp",
  imageAlt:
    "Infra Digest banner showing India's infrastructure: bridges, highways, metro rail, ports, energy and city skylines",
  bigChip: { value: "05", label: "Core domains" },
  smallChip: "For students, engineers & professionals",
};

export const MARQUEE_ITEMS = [
  "Transport & Logistics",
  "Energy",
  "Water & Sanitation",
  "Communication",
  "Social & Commercial Infrastructure",
];

export const ABOUT = {
  eyebrow: "About Infra Digest",
  title: "A pocket compendium on India's infrastructure sector.",
  paragraphs: [
    "Infrastructure-related information is often fragmented across multiple sources, making it difficult and time-consuming to locate reliable information. Infra Digest simplifies this process by consolidating essential infrastructure knowledge into a single organised platform.",
  ],
  listIntro:
    "Whether you are a student, researcher, engineer, policymaker or any other professional working in infrastructure sector, Infra Digest helps you explore India's infrastructure sector through structured and curated content covering:",
  domains: [
    "Transport & Logistics",
    "Energy",
    "Water & Sanitation",
    "Communication",
    "Social & Commercial Infrastructure",
  ],
  closing:
    "Aligned with the Government of India's Harmonised Master List (HML) of Infrastructure Sub-sectors, the app brings together essential infrastructure knowledge in a single, accessible platform.",
  image: "/images/about_improved.webp",
  image2x: "/images/about_improved-2x.webp",
  imageAlt:
    "Infra Digest cover art: a highway leading into a city at sunset, with a port crane, transmission towers and solar panels",
  features: [
    {
      title: "Consolidated Reference",
      text: "Infrastructure-related knowledge organised in one place.",
    },
    {
      title: "Offline",
      text: "The content is accessible offline after installation.",
    },
    {
      title: "Privacy-Conscious",
      text: "The app does not collect any user information.",
    },
    {
      title: "Regularly Updated",
      text: "Launched in May 2026, with the latest release in September 2026.",
    },
  ],
};

export const SECTORS_HEAD = {
  eyebrow: "Infrastructure Sectors",
  title: "Five domains. One complete picture.",
};

export const SECTORS = [
  {
    id: "transport",
    name: "Transport & Logistics",
    topics: [
      "Roads & Bridges",
      "Inland Waterways",
      "Airport",
      "Urban Public Transport",
      "Logistics",
    ],
  },
  {
    id: "energy",
    name: "Energy",
    topics: [
      "Electricity Generation",
      "Electricity Transmission",
      "Electricity Distribution",
      "Oil/Gas/LNG Storage",
      "Energy Storage Systems",
    ],
  },
  {
    id: "water",
    name: "Water & Sanitation",
    topics: [
      "Solid Waste Management",
      "Water Quality Parameters",
      "Water Treatment Plants",
    ],
  },
  {
    id: "communication",
    name: "Communication",
    topics: [
      "Telecommunication Towers",
      "Data & Cyber Security",
      "Information Security Standards",
      "Data Centres",
    ],
  },
  {
    id: "social",
    name: "Social & Commercial Infrastructure",
    topics: [
      "Sports Infrastructure",
      "Hospitals",
      "Terminal Markets",
      "Soil-Testing Laboratories",
      "Cold Chain",
      "Affordable Housing",
      "Affordable Rental Housing Complex",
    ],
  },
];

export const UPDATE = {
  heading: "Latest update",
  label: "September 2026",
  text: "The September 2026 release adds coverage of:",
  chips: [
    "Airports",
    "Inland Waterways",
    "Renewable Energy",
    "Data & Cybersecurity",
    "Information Security Standards",
    "Hydro & Thermal Power Generation Codes & Standards",
  ],
};

export const DOWNLOAD = {
  eyebrow: "Download the App",
  title: "Take Infra Digest with you.",
  sub: "The Pocket Compendium of India's Infrastructure Sector, now on Google Play",
  screenshot: "/images/home_improved.webp",
  screenshot2x: "/images/home_improved-2x.webp",
  screenshotAlt: "Infra Digest Android app home screen, September 2026 release",
  ctaTop: "Get it on",
  ctaMain: "Google Play",
};

export const CONTACT = {
  eyebrow: "Contact",
  title: "Questions, feedback or resource suggestions?",
  sub: "Reach out - feedback on the app and its resources is always welcome.",
  rows: [
    {
      icon: Mail,
      label: "Email",
      value: "bharatinfra.contact@gmail.com",
      href: "mailto:bharatinfra.contact@gmail.com",
      testid: "contact-email-row",
    },
    {
      icon: MapPin,
      label: "Based in",
      value: "India",
      testid: "contact-location-row",
    },
  ],
};
